import React, {useEffect, useState} from "react";
import {
    Customer,
    CustomerRestControllerApiFactory,
    SepaMandateList,
    SepaMandateRestControllerApiFactory
} from "arteco-api-client-ts";
import {FormContent} from "../../../../components/form/form_inputs/form_buttons";
import {FormInput} from "../../../../components/form/form_inputs/form_input";
import {
    Box,
    Button,
    Grid,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import {btnSx, EmptyState, Page, PageHeader, Panel, SoftChip, tableContainerSx, tableSx} from "../../../../components/kit";
import {dateToString} from "../../../../components/utils/dates";
import {SEPA_MANDATE_LABELS} from "../bill/labels";
import {useNavigate} from "react-router-dom";
import {FormAddress} from "../../../../components/form/form_inputs/form_address";
import {useForm, UseFormProps} from "../../../../components/form/form_model";
import {useApiManager} from "../../../../components/utils/api";
import FormToggle from "../../../../components/form/form_inputs/form_toggle";
import FormSelect from "../../../../components/form/form_inputs/form_select";
import {types} from "../../../../components/form/types";
import FormAutocomplete from "../../../../components/form/form_inputs/form_autocomplete";
import {RecordAttachments} from "../../../../components/record_attachments";

export const CustomerEditPage = () => {
    const manager = useApiManager();
    const navigate = useNavigate();
    const ctrl = manager.factory(CustomerRestControllerApiFactory);
    const mandateCtrl = manager.factory(SepaMandateRestControllerApiFactory);

    // El historial completo, revocados incluidos: es lo que hay que poder enseñar si el cliente
    // discute un cargo de hace dos años (#12845).
    const [mandates, setMandates] = useState<SepaMandateList[]>([]);


    const form = useForm({
        fetcher: (id) => ctrl.getCustomer(id),
        updater: (bean) => ctrl.saveCustomer(bean),
        remover: (id) => ctrl.deleteOrDismissCustomer(id),
        factory: () => {
            return {
                address: {}
            } as Customer;
        },
    } as UseFormProps<Customer>);

    const id = form.getId();

    // Mandatos del cliente, revocados incluidos (#12845). Desde que se creó la sección no se llegaban a
    // pedir y la ficha decía siempre «Sin mandatos» (#13893).
    useEffect(() => {
        if (id) {
            manager.execute(mandateCtrl.listSepaMandate(id, undefined, undefined, true))
                .then(list => setMandates(list || []))
                .catch(() => setMandates([]));
        }
    }, [id]);

    return (
        <Page>
            <PageHeader back="/app/admin/billing/customer"
                        title={id ? (form.value("shortName") || form.value("name") || "Cliente") : "Nuevo cliente"}
                        subtitle={id ? [form.value("name"), form.value("nif")].filter(Boolean).join(" · ") : undefined}/>

            <FormContent form={form} withoutPaper>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Stack spacing={3}>
                            <Panel title="Datos fiscales">
                                <FormInput field={"name"} label={"Nombre"}/>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <FormInput field={"shortName"} label={"Nombre corto"}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormInput field={"nif"} label={"NIF"}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormInput field={"vatEu"} label={"NIF intracomunitario (UE)"}/>
                                    </Grid>
                                </Grid>
                                <FormAddress field={"address"}/>
                            </Panel>

                            <Panel title="Facturación y cobro">
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormAutocomplete field={"accountingAccount"} label={"Cuenta contable"} type={types.accountingAccounts}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormInput field={"bankAccount"} label={"Cuenta del banco"}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormInput field={"paymentTermsDays"} type={"number"} label={"Plazo de pago (días)"}
                                                   helperText={"Vencimiento = fecha de emisión + estos días. Por defecto 30"}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormSelect field={"defaultPaymentMethod"} label={"Forma de cobro habitual"}
                                                    type={types.paymentMethods}/>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <FormInput field={"contractHourRate"} type={"number"} label={"Precio hora del contrato (€/h)"}
                                                   helperText={"Solo si el contrato fija precio por hora. Las facturas desde dedicaciones lo usan y el lote avisa de lo que no case"}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormAutocomplete field={"discount"} label={"Descuento"} type={types.discounts}/>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormToggle field={"anonBills"} label={"Facturas anónimas"}/>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <FormToggle field={"skipDedications"} label={"Ignorar dedicaciones"}/>
                                    </Grid>
                                </Grid>
                            </Panel>

                            {id && (
                                <Panel title="Mandatos SEPA"
                                       subtitle="Todo el historial, revocados incluidos: es lo que hay que poder enseñar si el cliente discute un cargo."
                                       action={<Button size={"small"} variant={"outlined"} startIcon={<AddRoundedIcon/>} sx={btnSx}
                                                       onClick={() => navigate(`/app/admin/billing/sepa-mandate/new?customerId=${id}`)}>
                                           Nuevo mandato
                                       </Button>}>
                                    {mandates.length === 0 ? (
                                        <EmptyState title="Sin mandatos" description="Este cliente no puede cobrarse por adeudo SEPA."/>
                                    ) : (
                                        <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                            <Table size={"small"} sx={{...tableSx, minWidth: 560}}>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell>Referencia</TableCell>
                                                        <TableCell>IBAN</TableCell>
                                                        <TableCell>Titular</TableCell>
                                                        <TableCell>Firma</TableCell>
                                                        <TableCell>Estado</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {mandates.map(m => {
                                                        const st = SEPA_MANDATE_LABELS[m.status || ""];
                                                        return (
                                                            <TableRow
                                                                key={m.id}
                                                                hover
                                                                sx={{cursor: "pointer"}}
                                                                onClick={() => navigate(`/app/admin/billing/sepa-mandate/${m.id}`)}>
                                                                <TableCell sx={{fontWeight: 600}}>{m.reference}</TableCell>
                                                                <TableCell sx={{fontFamily: "monospace"}}>{m.iban}</TableCell>
                                                                <TableCell>{m.debtorName}</TableCell>
                                                                <TableCell>{m.signatureDate ? dateToString(m.signatureDate) : "—"}</TableCell>
                                                                <TableCell>
                                                                    {st ? <SoftChip label={st.label} tone={st.tone}/> : m.status}
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </Panel>
                            )}
                        </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Stack spacing={3}>
                            <Panel title="Observaciones" subtitle="Salen como «Notas del cliente» en sus facturas y presupuestos.">
                                <FormInput field={"observation"} label={"Observaciones"} rows={10}/>
                            </Panel>
                            <Panel title="Mapeos" subtitle="Tarifa por hora → perfil, para las facturas desde dedicaciones.">
                                <FormInput field={"mappings"} label={"Mapeos"} rows={10}/>
                                <Box sx={{p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "divider",
                                    fontFamily: "monospace", fontSize: 12, whiteSpace: "pre-wrap", color: "text.secondary"}}>
                                    {`[role-by-rate]
48 -> Arquitecto
44 -> Technical Lead Advanced
42 -> Technical Lead Advanced
40 -> Technical Lead
36 -> Programador Senior Advanced
28 -> Programador Junior
09 -> Programador en Formación`}
                                </Box>
                            </Panel>
                        </Stack>
                    </Grid>
                </Grid>
            </FormContent>

            <Box sx={{mt: 3}}>
                <RecordAttachments recordType="CUSTOMER" recordId={id}/>
            </Box>
        </Page>
    );
};
